/**
 * ExpensesCategorySection Component
 * Collapsible expense breakdown by category within Cash & P&L tab.
 * Sortable by category name or amount.
 */

import { useState, useMemo } from 'react';
import { SectionCard } from './SectionCard';
import { CommentButton } from './CommentButton';
import { formatCurrency } from '@/utils/formatters';
import type { ExpenseSummaryResponse } from '@/api/cashApi';

interface ExpensesCategorySectionProps {
  expenses: ExpenseSummaryResponse['data'];
}

type SortField = 'name' | 'amount';
type SortDir = 'asc' | 'desc';

export function ExpensesCategorySection({ expenses }: ExpensesCategorySectionProps) {
  const [sortField, setSortField] = useState<SortField>('amount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => {
    return [...expenses.categories].sort((a, b) => {
      const cmp = sortField === 'amount'
        ? a.amount - b.amount
        : a.accountName.localeCompare(b.accountName);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [expenses.categories, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'amount' ? 'desc' : 'asc');
    }
  };

  const arrow = (field: SortField) => {
    if (sortField !== field) return '\u25B4';
    return sortDir === 'asc' ? '\u25B2' : '\u25BC';
  };

  return (
    <SectionCard
      title="Expenses by Category"
      badge={
        <span className="cash-pnl-dashboard__count">
          {formatCurrency(expenses.totalExpenses)} total
        </span>
      }
    >
      {sorted.length > 0 ? (
        <div className="cash-pnl-dashboard__table">
          <div className="cash-pnl-dashboard__table-header" style={{ cursor: 'pointer' }}>
            <span onClick={() => toggleSort('name')}>
              Category {arrow('name')}
            </span>
            <span onClick={() => toggleSort('amount')} style={{ textAlign: 'right' }}>
              Amount {arrow('amount')}
            </span>
          </div>
          {sorted.map((cat) => (
            <div key={cat.accountCode} className="cash-pnl-dashboard__table-row">
              <span className="cash-pnl-dashboard__account-name">
                {cat.accountName}
                <span className="cash-pnl-dashboard__percentage">
                  {cat.percentage.toFixed(1)}%
                </span>
                <CommentButton targetType="expense" targetId={cat.accountCode} />
              </span>
              <span className="cash-pnl-dashboard__balance">
                {formatCurrency(cat.amount)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="cash-pnl-dashboard__empty">No expense data available</p>
      )}
    </SectionCard>
  );
}
